import { baseUrl, getTokenFromLocalStorage } from "./Configuration";

export interface PublicApartmentOccupant {
    id: string;
    apartmentUnitId: string;
    name: string;
    email: string;
    phone?: string;
    isCosigner: boolean;
    createdAtUtc: string;
    updatedAtUtc: string;
}

export interface PublicApartmentUnit {
    id: string;
    apartmentTitle: string;
    zillowTitle?: string;
    building: string;
    unitName: string;
    floor?: number;
    line?: string;
    bedrooms?: number;
    rentalPrice?: number;
    currentRent?: number;
    description?: string;
    youTubeUrl?: string;
    occupants: PublicApartmentOccupant[];
    createdAtUtc: string;
    updatedAtUtc: string;
    appfolioLink?: string;
    accessCode?: string;
}

export async function GetAllPublicApartmentUnits(): Promise<PublicApartmentUnit[]> {
    const url = baseUrl + "/api/public/apartment-units";
    try {
        const response = await fetch(url, {
            headers: {
                Authorization: `Bearer ${await getTokenFromLocalStorage()}`,
            },
        });
        if (response.status === 401) {
            localStorage.clear();
            window.location.href = "/login";
            return [];
        }
        if (!response.ok) {
            throw new Error(`HTTP error! Status: ${response.status}`);
        }
        const data = await response.json();
        return data as PublicApartmentUnit[];
    } catch (error) {
        console.error("Error fetching public apartment units:", error);
        throw error;
    }
}

export async function GetPublicApartmentUnitById(id: string): Promise<PublicApartmentUnit> {
    const url = baseUrl + `/api/public/apartment-units/${id}`;
    // Fetch needs the expected API key if we call it from anonymous context, 
    // but we removed the API Key requirement when called via backend or we can just pass a dummy one if it expects it.
    // Wait, GetById has [AllowAnonymous] and expects X-Api-Key. The backend expects _config["PublicApi:ApiKey"].
    // Let's use the token which might bypass? No, AllowAnonymous endpoints with explicit ApiKey checks will fail without it.
    // The previous implementation in backend: 
    // if (string.IsNullOrWhiteSpace(apiKey) || !string.Equals(apiKey, expectedApiKey)) return Unauthorized();
    // Wait, if it's called from UI, the UI might not have the PublicApi:ApiKey.
    // Let's look at the backend GetById. Yes, it has the API key check. I'll need to remove it or pass it. 
    // I should fix the backend to not require API key or allow Bearer token.
    try {
        const response = await fetch(url, {
            headers: {
                Authorization: `Bearer ${await getTokenFromLocalStorage()}`,
            },
        });
        if (response.status === 401) {
            localStorage.clear();
            window.location.href = "/login";
            throw new Error("Unauthorized");
        }
        if (!response.ok) {
            throw new Error(`HTTP error! Status: ${response.status}`);
        }
        const data = await response.json();
        return data as PublicApartmentUnit;
    } catch (error) {
        console.error("Error fetching apartment by id:", error);
        throw error;
    }
}

export async function UpdatePublicApartmentUnit(id: string, payload: any): Promise<any> {
    const url = baseUrl + `/api/public/apartment-units/${id}`;
    try {
        const response = await fetch(url, {
            method: "PUT",
            headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${await getTokenFromLocalStorage()}`,
            },
            body: JSON.stringify(payload),
        });
        if (response.status === 401) {
            localStorage.clear();
            window.location.href = "/login";
            throw new Error("Unauthorized");
        }
        if (!response.ok) {
            throw new Error(`HTTP error! Status: ${response.status}`);
        }
        return await response.json();
    } catch (error) {
        console.error("Error updating apartment:", error);
        throw error;
    }
}

export async function DeletePublicApartmentUnit(id: string): Promise<void> {
    const url = `${baseUrl}/api/public/apartment-units/${id}`;
    const token = await getTokenFromLocalStorage();

    const response = await fetch(url, {
        method: 'DELETE',
        headers: {
            'Authorization': `Bearer ${token}`
        }
    });

    if (!response.ok) {
        const text = await response.text();
        throw new Error(`Failed to delete apartment unit: ${text}`);
    }
}
